import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import BookingLayoutDesigner from './BookingLayoutDesigner';

interface AddScreeningProps {
  onClose: () => void;
}

export default function AddScreening({ onClose }: AddScreeningProps) {
  const [movies, setMovies] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    movie_id: '',
    start_time: '',
    location: '',
    available_seats: 100,
    silver_percentage: 60,
    gold_percentage: 30,
    diamond_percentage: 10,
    enable_seatsplitting: true,
    seat_numbering_start: 'top-left',
  });
  const [loading, setLoading] = useState(false);
  const [loadingMovies, setLoadingMovies] = useState(true);
  const [showDesigner, setShowDesigner] = useState(false);
  const [layoutDraft, setLayoutDraft] = useState<{ name: string; rows: number; cols: number; seats: boolean[][] } | null>(null);

  useEffect(() => {
    async function fetchMovies() {
      try {
        const { data, error } = await supabase
          .from('movies')
          .select('id, title')
          .order('title', { ascending: true });

        if (error) throw error;
        setMovies(data || []);
      } catch (error) {
        console.error('Error fetching movies:', error);
        toast.error('Failed to load movies');
      } finally {
        setLoadingMovies(false);
      }
    }

    fetchMovies();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: (type === 'number' || type === 'range') ? Number(value) : value
    }));
  };

  /**
   * Linked slider handlers: when one slider moves, the others auto-adjust
   * so silver + gold + diamond always === 100%.
   */
  const handleSilverSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSilver = Math.min(Number(e.target.value), 100);
    let newGold = formData.gold_percentage;
    const remaining = 100 - newSilver;
    if (newGold > remaining) newGold = Math.max(0, remaining);
    setFormData(prev => ({
      ...prev,
      silver_percentage: newSilver,
      gold_percentage: newGold,
      diamond_percentage: 100 - newSilver - newGold,
    }));
  };

  const handleGoldSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newGold = Math.min(Number(e.target.value), 100);
    let newSilver = formData.silver_percentage;
    const remaining = 100 - newGold;
    if (newSilver > remaining) newSilver = Math.max(0, remaining);
    setFormData(prev => ({
      ...prev,
      gold_percentage: newGold,
      silver_percentage: newSilver,
      diamond_percentage: 100 - newSilver - newGold,
    }));
  };

  const handleDiamondSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDiamond = Math.min(Number(e.target.value), 100);
    const otherTotal = 100 - newDiamond;
    const currentOther = formData.silver_percentage + formData.gold_percentage;
    if (currentOther === 0) {
      const newSilver = Math.floor(otherTotal / 2);
      setFormData(prev => ({
        ...prev,
        silver_percentage: newSilver,
        gold_percentage: otherTotal - newSilver,
        diamond_percentage: newDiamond,
      }));
    } else {
      const silverRatio = formData.silver_percentage / currentOther;
      const newSilver = Math.round(otherTotal * silverRatio);
      setFormData(prev => ({
        ...prev,
        silver_percentage: newSilver,
        gold_percentage: otherTotal - newSilver,
        diamond_percentage: newDiamond,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.movie_id || !formData.start_time || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.enable_seatsplitting) {
      if (formData.silver_percentage + formData.gold_percentage + formData.diamond_percentage !== 100) {
        toast.error('Seat percentages must total 100%');
        return;
      }
    }

    setLoading(true);

    try {
      const { data: screeningData, error: screeningError } = await supabase
        .from('screenings')
        .insert([
          {
            movie_id: Number(formData.movie_id),
            start_time: new Date(formData.start_time).toISOString(),
            location: formData.location,
            available_seats: formData.available_seats,
            salt: 'default_salt',
            enable_seatsplitting: formData.enable_seatsplitting,
            seat_numbering_start: formData.seat_numbering_start
          }
        ])
        .select();

      if (screeningError) throw screeningError;

      const screeningId = screeningData[0].id;

      let silverTotal = formData.available_seats;
      let goldTotal = 0;
      let diamondTotal = 0;
      if (formData.enable_seatsplitting) {
        silverTotal = Math.floor(formData.available_seats * (formData.silver_percentage / 100));
        goldTotal = Math.floor(formData.available_seats * (formData.gold_percentage / 100));
        diamondTotal = formData.available_seats - silverTotal - goldTotal;
      }

      const { error: seatsError } = await supabase
        .from('seatssplitting')
        .insert([
          {
            screening_id: screeningId,
            silver_total: silverTotal,
            silver_booked: 0,
            gold_total: goldTotal,
            gold_booked: 0,
            diamond_total: diamondTotal,
            diamond_booked: 0
          }
        ]);

      if (seatsError) throw seatsError;

      if (layoutDraft) {
        const { error: layoutErr } = await supabase
          .from('seat_layouts')
          .upsert({
            screening_id: screeningId,
            layout_name: layoutDraft.name,
            rows: layoutDraft.rows,
            cols: layoutDraft.cols,
            seats: layoutDraft.seats,
          }, { onConflict: 'screening_id' });
        if (layoutErr) throw layoutErr;
      }

      toast.success('Screening added successfully!');
      onClose();
    } catch (error) {
      console.error('Error adding screening:', error);
      toast.error('Failed to add screening');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg shadow-2xl w-full max-w-[95vw] md:max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-200 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h2 className="text-2xl font-bold text-white">Add New Screening</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-300">
              <input
                type="checkbox"
                name="enable_seatsplitting"
                checked={formData.enable_seatsplitting}
                onChange={(e) => setFormData(prev => ({ ...prev, enable_seatsplitting: e.target.checked }))}
                className="rounded border-gray-600 bg-gray-700"
              />
              Enable seat splitting
            </label>
          </div>
          <div>
            <button
              type="button"
              onClick={() => setShowDesigner(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              Design Seat Layout
            </button>
            {layoutDraft && (
              <p className="mt-2 text-xs text-gray-400">Layout saved: {layoutDraft.name} ({layoutDraft.rows}x{layoutDraft.cols})</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Movie *
            </label>
            <select
              name="movie_id"
              value={formData.movie_id}
              onChange={handleChange}
              disabled={loadingMovies}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500 disabled:opacity-50"
            >
              <option value="">{loadingMovies ? 'Loading movies...' : 'Select a movie'}</option>
              {movies.map(movie => (
                <option key={movie.id} value={movie.id}>
                  {movie.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Start Time *
            </label>
            <input
              type="datetime-local"
              name="start_time"
              value={formData.start_time}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Location *
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., Cinema Hall A, Mumbai"
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Total Available Seats
            </label>
            <input
              type="number"
              name="available_seats"
              value={formData.available_seats}
              onChange={handleChange}
              min="1"
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
            />
          </div>

          {formData.enable_seatsplitting && (
            <div className="bg-gray-700/50 rounded-lg p-4 space-y-4">
              <h3 className="text-sm font-semibold text-gray-300">Seat Allocation</h3>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Silver Seats: {formData.silver_percentage}%
                </label>
                <input
                  type="range"
                  value={formData.silver_percentage}
                  onChange={handleSilverSlider}
                  min="0"
                  max="100"
                  className="w-full h-2 bg-gray-600 rounded-lg cursor-pointer"
                />
                <p className="text-xs text-gray-400 mt-1">
                  ({Math.floor(formData.available_seats * (formData.silver_percentage / 100))} seats)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Gold Seats: {formData.gold_percentage}%
                </label>
                <input
                  type="range"
                  value={formData.gold_percentage}
                  onChange={handleGoldSlider}
                  min="0"
                  max="100"
                  className="w-full h-2 bg-gray-600 rounded-lg cursor-pointer"
                />
                <p className="text-xs text-gray-400 mt-1">
                  ({Math.floor(formData.available_seats * (formData.gold_percentage / 100))} seats)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Diamond Seats: {formData.diamond_percentage}%
                </label>
                <input
                  type="range"
                  value={formData.diamond_percentage}
                  onChange={handleDiamondSlider}
                  min="0"
                  max="100"
                  className="w-full h-2 bg-gray-600 rounded-lg cursor-pointer"
                />
                <p className="text-xs text-gray-400 mt-1">
                  ({formData.available_seats - Math.floor(formData.available_seats * (formData.silver_percentage / 100)) - Math.floor(formData.available_seats * (formData.gold_percentage / 100))} seats)
                </p>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-400 pt-2 border-t border-gray-600">
                <span>Total: {formData.available_seats} / {formData.available_seats} seats</span>
                <span className="text-green-400 ml-auto">✓ Always 100%</span>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || loadingMovies}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50"
            >
              {loading ? 'Adding...' : 'Add Screening'}
            </button>
          </div>
        </form>
      </div>
      <BookingLayoutDesigner
        show={showDesigner}
        initialLayout={layoutDraft ? { rows: layoutDraft.rows, cols: layoutDraft.cols, seats: layoutDraft.seats } : null}
        initialName={layoutDraft?.name || ''}
        initialSeatNumberingStart={formData.seat_numbering_start}
        onClose={() => setShowDesigner(false)}
        onSeatNumberingStartChange={(value) => {
          setFormData(prev => ({ ...prev, seat_numbering_start: value }));
        }}
        onSave={(name, layout, seatNumberingStart) => {
          setLayoutDraft({ name, rows: layout.rows, cols: layout.cols, seats: layout.seats });
          setFormData(prev => ({ ...prev, seat_numbering_start: seatNumberingStart }));
        }}
      />
    </div>,
    document.body
  );
}
